/**
 * «Сейчас в районе» (agroai-0e8, журнал №1560): короткий ответ хокиму «что
 * происходит сейчас» — первым блоком под шапкой, до разбора проблем.
 *
 * Три части: начатые агроциклы района и их этап (по окнам плана, сервер);
 * работы выбранного агроцикла с открытым окном — срок и «выполнено X из Y га
 * работ»; что начнётся в ближайшие 14 дней. Только факты с основой «из N»,
 * без оценок «успеваем / отстаём»: темп — ниже, в «Темпе кампании».
 * Заменил «Сейчас в поле» и KPI текущей кампании (дубли одного и того же окна).
 */
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { Card } from '../../ui/Card';
import { Icon } from '../../ui/Icon';
import { dateDay, dateShort, num, pct } from '../../lib/format';
import { defineMessages } from '../../../i18n/define';
import { useT } from '../../../i18n/hooks';

const m = defineMessages({
  title: { id: 'district.now.title', defaultMessage: 'Сейчас в районе', description: 'Заголовок первого блока «Обзора района» (хоким, менеджер): что идёт в районе на дату среза — агроциклы, работы в поле, что скоро' },
  info: {
    id: 'district.now.info',
    defaultMessage:
      'Что идёт на дату среза. Агроциклы — начатые агроциклы района и их этап по окнам работ плана. «В поле» — работы выбранного агроцикла с открытым окном, первыми — те, чьё окно закрывается раньше; полоса — площадь работ с отчётом «выполнено» (бледнее — «в процессе») из всей площади работ окна. Пока окно идёт, отсутствие отчёта значит «ещё не срок», а не «не сделано». «Скоро» — работы, чьё окно откроется в ближайшие 14 дней.',
    description: 'Подсказка «i» блока «Сейчас в районе»: откуда агроциклы и этапы, как читать полосу работы, что такое «Скоро»',
  },
  calendar: { id: 'district.now.calendar', defaultMessage: 'Календарь сезона →', description: 'Ссылка в шапке блока «Сейчас в районе» на календарь сезона; со стрелкой, коротко' },
  cyclesLabel: { id: 'district.now.cyclesLabel', defaultMessage: 'Идут агроциклы', description: 'Подпись строки с начатыми агроциклами района в блоке «Сейчас в районе»; коротко' },
  cycleStage: { id: 'district.now.cycleStage', defaultMessage: 'этап «{stage}»', description: 'Текущий этап агроцикла в блоке «Сейчас в районе»; {stage} — название этапа из данных («Уборка»); строчными' },
  cycleUntil: { id: 'district.now.cycleUntil', defaultMessage: 'до {date}', description: 'Конец окна агроцикла в блоке «Сейчас в районе»; {date} — дата без года («30 окт»); коротко' },
  cycleShow: { id: 'district.now.cycleShow', defaultMessage: 'Показать обзор агроцикла {name}', description: 'aria-label кнопки-агроцикла в блоке «Сейчас в районе»: переключает весь обзор на этот агроцикл; {name} — «Пшеница-2026-2027»' },
  cycleShown: { id: 'district.now.cycleShown', defaultMessage: 'на экране', description: 'Пометка у агроцикла, который сейчас выбран на «Обзоре района»; строчными, коротко' },
  fieldLabel: { id: 'district.now.fieldLabel', defaultMessage: 'В поле · {cycle}', description: 'Подзаголовок колонки «работы с открытым окном» в блоке «Сейчас в районе»; {cycle} — имя агроцикла («Хлопок-2026»)' },
  dueToday: { id: 'district.now.dueToday', defaultMessage: 'до {date} · последний день', description: 'Срок окна работы, когда окно закрывается в дату среза; {date} — дата без года' },
  dueIn: {
    id: 'district.now.dueIn',
    defaultMessage: 'до {date} · {count, plural, one {# день} few {# дня} many {# дней} other {# дня}}',
    description: 'Срок окна работы и сколько дней осталось; {date} — дата без года, {count} — число дней',
  },
  doneArea: { id: 'district.now.doneArea', defaultMessage: 'выполнено {done} из {total} га работ', description: 'Ход работы с открытым окном в гектарах работ; {done}/{total} — готовые числа' },
  doneCount: {
    id: 'district.now.doneCount',
    defaultMessage: 'выполнено {done} из {total, plural, one {# работы на поле} few {# работ на полях} many {# работ на полях} other {# работы на полях}}',
    description: 'Ход работы с открытым окном по числу работ, когда площадь не указана; {done} — готовое число, {total} — всего',
  },
  barLabel: { id: 'district.now.barLabel', defaultMessage: 'Выполнено {share} площади работ окна', description: 'aria-label полосы хода работы для чтеца с экрана; {share} — процент («94 %»)' },
  fieldEmpty: { id: 'district.now.fieldEmpty', defaultMessage: 'На {date} ни одно окно работ агроцикла не идёт.', description: 'Пустая колонка «В поле»: на дату среза нет открытых окон; {date} — дата среза' },
  soonLabel: { id: 'district.now.soonLabel', defaultMessage: 'Скоро · 14 дней', description: 'Подзаголовок колонки «что начнётся в ближайшие 14 дней» в блоке «Сейчас в районе»; коротко' },
  soonFrom: { id: 'district.now.soonFrom', defaultMessage: 'с {date}', description: 'Начало окна будущей работы в колонке «Скоро»; {date} — дата без года («12 окт»)' },
  soonNone: { id: 'district.now.soonNone', defaultMessage: 'Новых окон работ в ближайшие 14 дней нет.', description: 'Пустая колонка «Скоро»: в ближайшие 14 дней ни одно окно работ агроцикла не открывается' },
  cycleEnds: { id: 'district.now.cycleEnds', defaultMessage: 'Агроцикл {name} закрывается {date}.', description: 'Строка колонки «Скоро»: окно агроцикла кончается в ближайший месяц; {name} — имя агроцикла, {date} — дата без года' },
});

export interface ActiveCycle {
  key: string;
  name: string;
  stage: string | null;
  windowTo: string | null;
}

export interface FieldWindow {
  key: string;
  name: string;
  windowTo: string | null;
  /** Дней до конца окна от даты среза (0 — последний день). */
  daysLeft: number | null;
  /** Ход в гектарах работ; `null` — площадь не указана, ход по числу работ. */
  area: { done: number; inProgress: number; total: number } | null;
  count: { done: number; inProgress: number; total: number };
  href: string | null;
}

export interface SoonWork {
  key: string;
  name: string;
  from: string;
  href: string | null;
}

const share = (part: number, total: number) => (total > 0 ? Math.min(100, Math.max(0, (100 * part) / total)) : 0);

function Progress({ w }: { w: FieldWindow }) {
  const t = useT();
  const basis = w.area ?? w.count;
  const done = share(basis.done, basis.total);
  const active = share(basis.inProgress, basis.total);
  return (
    <span className="district-now__bar" role="img" aria-label={t(m.barLabel, { share: pct(done) })}>
      <span className="district-now__bar-done" style={{ width: `${done}%` }} />
      <span className="district-now__bar-active" style={{ width: `${Math.min(active, 100 - done)}%` }} />
    </span>
  );
}

export function NowSummary({ asOf, cycles, selected, onCycle, cycleName, cycleEnd, plan, windows, soon, calendarHref, print }: {
  asOf: string;
  cycles: ActiveCycle[];
  selected: string;
  onCycle: (key: string) => void;
  cycleName: string;
  /** Конец окна выбранного агроцикла, если он в ближайший месяц. */
  cycleEnd: string | null;
  /** «запущено K из M работ плана · впереди N» (строка агроцикла) или пусто. */
  plan: string;
  windows: FieldWindow[];
  soon: SoonWork[];
  calendarHref: string;
  print: boolean;
}) {
  const t = useT();
  return (
    <Card title={t(m.title)} info={t(m.info)} aside={print ? null : <Link to={calendarHref}>{t(m.calendar)}</Link>}>
      {cycles.length ? (
        <div className="district-now__cycles">
          <span className="district-now__label">{t(m.cyclesLabel)}</span>
          <ul className="district-now__cycle-list">
            {cycles.map(c => {
              const body = (
                <>
                  <strong>{c.name}</strong>
                  {c.stage ? <span>{t(m.cycleStage, { stage: c.stage })}</span> : null}
                  {c.windowTo ? <span className="district-now__muted">{t(m.cycleUntil, { date: dateDay(c.windowTo) })}</span> : null}
                  {c.key === selected && !print ? <span className="district-now__shown">{t(m.cycleShown)}</span> : null}
                </>
              );
              return (
                <li key={c.key}>
                  {c.key === selected || print
                    ? <span className={`district-now__cycle${c.key === selected ? ' district-now__cycle--on' : ''}`}>{body}</span>
                    : <button type="button" className="district-now__cycle" aria-label={t(m.cycleShow, { name: c.name })} onClick={() => onCycle(c.key)}>{body}</button>}
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
      <div className="district-now__cols">
        <section className="district-now__col">
          <h3 className="district-now__label">{t(m.fieldLabel, { cycle: cycleName })}</h3>
          {plan ? <p className="district-now__muted district-now__plan">{plan}</p> : null}
          {windows.length ? (
            <ul className="district-now__works">
              {windows.map(w => {
                const body = (
                  <>
                    <span className="district-now__work-name">{w.name}</span>
                    <span className="district-now__work-progress">
                      <Progress w={w} />
                      <span className="district-now__muted">
                        {w.area
                          ? t(m.doneArea, { done: num(Math.round(w.area.done)), total: num(Math.round(w.area.total)) })
                          : t(m.doneCount, { done: num(w.count.done), total: w.count.total })}
                      </span>
                    </span>
                    <span className={`district-now__due${w.daysLeft !== null && w.daysLeft <= 3 ? ' district-now__due--near' : ''}`}>
                      {w.daysLeft === 0 ? t(m.dueToday, { date: dateDay(w.windowTo) }) : w.daysLeft !== null ? t(m.dueIn, { date: dateDay(w.windowTo), count: w.daysLeft }) : null}
                    </span>
                  </>
                );
                return (
                  <li key={w.key}>
                    {w.href && !print ? (
                      <Link to={w.href} className="district-now__work linkcard">
                        {body}
                        <span className="linkcard__go" aria-hidden="true"><Icon icon={ArrowUpRight} size={16} /></span>
                      </Link>
                    ) : <div className="district-now__work">{body}</div>}
                  </li>
                );
              })}
            </ul>
          ) : <p className="district-now__muted">{t(m.fieldEmpty, { date: dateShort(asOf) })}</p>}
        </section>
        <section className="district-now__col district-now__col--soon">
          <h3 className="district-now__label">{t(m.soonLabel)}</h3>
          {soon.length ? (
            <ul className="district-now__soon">
              {soon.map(s => (
                <li key={s.key}>
                  <span className="district-now__soon-date">{t(m.soonFrom, { date: dateDay(s.from) })}</span>
                  {s.href && !print ? <Link to={s.href}>{s.name}</Link> : <span>{s.name}</span>}
                </li>
              ))}
            </ul>
          ) : <p className="district-now__muted">{t(m.soonNone)}</p>}
          {cycleEnd ? <p className="district-now__muted">{t(m.cycleEnds, { name: cycleName, date: dateDay(cycleEnd) })}</p> : null}
        </section>
      </div>
    </Card>
  );
}
